import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ArrowRight, MapPin, Star, ThumbsUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GreenCircle, Humid, Temp, UV, Wind, Chat } from '@/assets/icon';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiZGFmaWRhZmkyNSIsImEiOiJjbTdsczg0Y3QwYnEwMmpwemIxZ2h4dHY0In0.dcTST_LLYTlp-t_oUkB0dw';

const InsightsSection: React.FC = () => {
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [114.15, 22.40], // Hong Kong coordinates - centered on New Territories
      zoom: 11
    });

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <div className="px-4 sm:px-8 lg:px-16 xl:px-24 py-12">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p
            className="text-gray-500 mb-2"
            style={{
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 400
            }}
          >
            What's happening outdoors?
          </p>
          <h2
            className="text-black"
            style={{
              fontFamily: 'Poppins',
              fontSize: '40px',
              fontWeight: 700,
              lineHeight: '48px'
            }}
          >
            Explore Real-Time Campsite Insights.
          </h2>
        </div>
        <button
          onClick={() => navigate('/monitor')}
          className="flex items-center gap-2 px-6 py-3 border-2 border-black rounded-full hover:bg-black hover:text-white transition-all"
          style={{
            fontFamily: 'Poppins',
            fontSize: '16px',
            fontWeight: 500
          }}
        >
          View more
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Single Combined Layout */}
      <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 h-[500px]">
          {/* Left: Heatmap */}
          <div className="bg-white p-6 h-full relative">
            {/* Mapbox Map */}
            <div ref={mapContainer} className="w-full h-full rounded-2xl" />

            {/* Text and Button Overlay */}
            <div className="absolute top-8 left-8 right-8 flex items-start justify-between z-10">
              <div className="">
                <h3
                  className="text-black"
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: '20px',
                    fontWeight: 600
                  }}
                >
                  Crowd Heatmap
                </h3>
                <p
                  className="text-gray-600"
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 400
                  }}
                >
                  15:14:33 8/10/2025
                </p>
              </div>
              {/* <button className="bg-white/90 backdrop-blur-sm p-3 rounded-xl shadow-lg hover:bg-white transition-all"> */}
                {/* <ChevronDown className="w-5 h-5" /> */}
              {/* </button> */}
            </div>
          </div>

          {/* Right: Campsite Details */}
          <div className="p-6">
          {/* Campsite Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between mb-3">
              <h3
                className="text-black flex-1"
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '24px',
                  fontWeight: 600,
                  lineHeight: '32px'
                }}
              >
                BBQ Site at Hok Tau Reservoir
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '16px',
                      fontWeight: 600
                    }}
                  >
                    4,6(5)
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ThumbsUp className="w-5 h-5 text-orange-500 fill-orange-500" />
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '16px',
                      fontWeight: 600
                    }}
                  >
                    94%
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <MapPin className="w-4 h-4" />
              <span
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                Kwan Tei
              </span>
            </div>

            <p
              className="text-gray-600 mb-4"
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px'
              }}
            >
              A peaceful campsite by Hok Tau Reservoir, offering barbecue pits, open areas
              for tents, and access to nearby hiking trails. Ideal for picnics, group gatherings,
              and quiet outdoor recreation.
            </p>

            {/* Occupancy */}
            <div className="flex items-center gap-3">
              <span
                className="text-black"
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '16px',
                  fontWeight: 600
                }}
              >
                Occupancy: 1/20
              </span>
              <span
                className="px-3 py-1 rounded-full text-white"
                style={{
                  backgroundColor: '#719C40',
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                Plenty of Space
              </span>
            </div>
          </div>

          {/* Metrics */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            {/* Air Quality */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={GreenCircle} className="w-6 h-6" alt="Air Quality" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Air Quality: PM2.5: 35µg/m³
                </span>
              </div>
              <span
                className="text-gray-600"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                Moderate
              </span>
            </div>

            {/* Wind Speed */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={Wind} className="w-6 h-6" alt="Wind Speed" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Wind Speed
                </span>
              </div>
              <span
                className="text-gray-600"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                Light Breeze
              </span>
            </div>

            {/* Humidity */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={Humid} className="w-6 h-6" alt="Humidity" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Humidity
                </span>
              </div>
              <span
                className="text-gray-600"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                82%
              </span>
            </div>

            {/* Temperature */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={Temp} className="w-6 h-6" alt="Temperature" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Temperature
                </span>
              </div>
              <span
                className="text-gray-600"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                27°
              </span>
            </div>

            {/* UV Index */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={UV} className="w-6 h-6" alt="UV Index" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  UV Index: 6
                </span>
              </div>
              <span
                className="text-gray-600"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                Moderate
              </span>
            </div>

            {/* Outdoor Suitability */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={Chat} className="w-6 h-6" alt="Outdoor Suitability" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Outdoor Suitability
                </span>
              </div>
              <span
                className="text-gray-600"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400
                }}
              >
                Good for Outdoor Activity
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default InsightsSection;
